import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Button, Stack } from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { UserProfile } from '@trnsact/trnsact-shared-types';
import { accountSelectors } from 'redux/accountReducer';
import { DateFormats } from 'constants/common';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { TabsOption } from 'global';
import { usePdf } from 'hooks/usePdf';
import trnsactLogo from 'assets/img/trnsact-logo-light.png';
import { LenderPerformanceFilterFormValues, LenderPerformanceTab } from '../../../types';
import { LENDER_PERFORMANCE_DEFAULT_FILTERS } from '../../../constants';
import { mapFiltersToReportInput } from '../../../lib';
import { buildPdfHeaderTemplate, buildPdfFooterTemplate } from '../../../lib/lenderPerformance/pdfDocumentChrome';
import { LenderPerformanceFilters } from './LenderPerformanceFilters';
import { ConversionFunnel } from './conversionFunnel';
import { ApprovalTerms } from './approvalTerms';
import { CardBackground } from 'components/CardBackground/CardBackground';
import makeStyles from '@mui/styles/makeStyles';
import { USE_PDF_SELECTOR } from 'hooks/usePdf';

const REPORT_NAME_BY_TAB: Record<LenderPerformanceTab, string> = {
  [LenderPerformanceTab.ConversionFunnel]: 'Conversion Funnel',
  [LenderPerformanceTab.ApprovalTerms]: 'Approval Terms Analytics',
};

export const LenderPerformance = () => {
  const classes = useStyles();
  const { contentRef, handleDownloadPdf } = usePdf();
  const account = useSelector(accountSelectors.account);
  const userProfile = useSelector((state: any) => state.userProfile) as UserProfile;

  const [currentTab, setCurrentTab] = useState<LenderPerformanceTab>(LenderPerformanceTab.ConversionFunnel);
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [isReportLoading, setIsReportLoading] = useState(true);

  const { control, setValue, reset } = useForm<LenderPerformanceFilterFormValues>({
    defaultValues: LENDER_PERFORMANCE_DEFAULT_FILTERS,
  });

  useEffect(() => {
    // Puppeteer's header/footer frame can't load external/relative image URLs, so the logo
    // must be inlined as a data URL before it's embedded in the header template string.
    fetch(trnsactLogo)
      .then(response => response.blob())
      .then(
        blob =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .then(setLogoDataUrl);
  }, []);

  const filters = useWatch({ control }) as LenderPerformanceFilterFormValues;
  const input = useMemo(() => mapFiltersToReportInput(filters), [filters]);

  const handleRangeChange = async (range: { endDate: moment.Moment; startDate: moment.Moment }) => {
    setValue('from', range.startDate);
    setValue('to', range.endDate);
  };

  const handleDownloadPdfClick = () => {
    const dateRangeLabel = `${filters.from.format(DateFormats.Medium)} – ${filters.to.format(DateFormats.Medium)}`;

    handleDownloadPdf({
      displayHeaderFooter: true,
      headerTemplate: buildPdfHeaderTemplate({
        reportName: REPORT_NAME_BY_TAB[currentTab],
        accountName: account?.name ?? '',
        lenders: filters.lenders,
        salesReps: filters.salesReps,
        locations: filters.locations,
        dateRangeLabel,
        logoDataUrl,
      }),
      footerTemplate: buildPdfFooterTemplate({
        generatedByName: `${userProfile?.firstName ?? ''} ${userProfile?.lastName ?? ''}`.trim(),
        generatedAtLabel: moment().format('MMM D, YYYY [at] h:mm A'),
      }),
      // The lambda's default header margin (64px) is too short for this report's two-row
      // header (title/account/logo + 2x2 meta grid), which caused it to overlap page content.
      margin: { top: '110px', right: '24px', bottom: '64px', left: '24px' },
    });
  };

  const tabs: TabsOption<LenderPerformanceTab>[] = [
    {
      label: 'Conversion Funnel',
      value: LenderPerformanceTab.ConversionFunnel,
      component: <ConversionFunnel input={input} onLoadingChange={setIsReportLoading} />,
    },
    {
      label: 'Approval Terms Analytics',
      value: LenderPerformanceTab.ApprovalTerms,
      component: <ApprovalTerms input={input} onLoadingChange={setIsReportLoading} />,
    },
  ];

  return (
    <Stack ref={contentRef} gap="0.5rem">
      <Box data-pdf-exclude>
        <LenderPerformanceFilters control={control} onRangeChange={handleRangeChange} onReset={() => reset()} />
      </Box>

      <Box className={classes.pdfScope}>
        <TabsContainer<LenderPerformanceTab>
          tabs={tabs}
          initialTab={LenderPerformanceTab.ConversionFunnel}
          onTabChange={nextTab => {
            setIsReportLoading(true);
            setCurrentTab(nextTab);
          }}
          TabsLabelWrapper={({ children }) => (
            <Box data-pdf-exclude>
              <CardBackground>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  {children}

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloudDownloadOutlinedIcon />}
                    onClick={handleDownloadPdfClick}
                    disabled={isReportLoading}
                  >
                    Download PDF
                  </Button>
                </Stack>
              </CardBackground>
            </Box>
          )}
        />
      </Box>
    </Stack>
  );
};

const useStyles = makeStyles({
  pdfScope: {
    [`${USE_PDF_SELECTOR} &`]: {
      '& .tableContainer': { overflow: 'visible !important' },
      '& .tableContainer td': { padding: '4px 6px !important', fontSize: '11px !important' },
      '& .tableContainer th': { padding: '4px 6px !important', fontSize: '11px !important' },
    },
  },
});
